import {
  ActionType,
  Action,
  Beneficiary,
  Pharmacy,
  SearchResultPlan,
  LowIncomeSubsidyOption,
  LowIncomeSubsidyStatus,
  LowIncomeSubsidyAmount,
  Plan,
  FullPlanId,
  CoverageInfo,
  OtherBenefit,
  OtherBenefitCategory,
} from "../@types";
import {
  parsePlanLongIdIntoUrlPartForApi,
  makePlanLongId,
  logError,
} from "../helpers";
import { getPlanCard, getPlanInfoForNextYear } from "../api";
import { Dispatch } from "react";

export const fetchCurrentPlan = (
  beneficiary: Beneficiary | undefined,
  pharmacies: Pharmacy[],
  lis: LowIncomeSubsidyStatus,
  dispatch: Dispatch<Action>,
  nextYearPlanId?: FullPlanId,
  futureLis?: LowIncomeSubsidyStatus
): Promise<void> => {
  if (!beneficiary || !beneficiary.current_coverage.length) {
    return Promise.resolve();
  }

  const coverage = {
    ...beneficiary.current_coverage[0],
    contract_year: beneficiary.current_coverage[0].coverage_year,
  };

  if (
    !coverage.contract_id ||
    !coverage.contract_year ||
    !coverage.plan_id ||
    !coverage.segment_id
  ) {
    return Promise.resolve();
  }

  const apiUrl = parsePlanLongIdIntoUrlPartForApi(makePlanLongId(coverage));
  const npis = pharmacies.map(p => p.npi);

  if (nextYearPlanId) {
    const longIdForNextYear = parsePlanLongIdIntoUrlPartForApi(
      makePlanLongId(nextYearPlanId)
    );
    getPlanCard(longIdForNextYear, npis, futureLis || lis)
      .then(plan => {
        dispatch({
          type: ActionType.ADD_NEXT_YEAR_COVERAGE,
          payload: plan,
        });
      })
      .catch(e => {
        logError(
          "Failed to retrieve plan card for next year plan (planHelpers/fetchCurrentPlan)",
          e
        );
      });
  }

  return getPlanCard(apiUrl, npis, lis)
    .then(plan => {
      dispatch({
        type: ActionType.ADD_CURRENT_COVERAGE,
        payload: plan,
      });
    })
    .catch(e => {
      logError(
        "Failed to retrieve plan card (planHelpers/fetchCurrentPlan)",
        e
      );
    });
};

export const shouldSuppressPlanBenefits = (
  plan: Plan | SearchResultPlan
): boolean => {
  const { redactions } = plan;

  return redactions.some(
    redaction =>
      redaction.type === "suppression" &&
      ["bid", "contract", "ma-network", "plan_correction", "other"].includes(
        redaction.issue
      )
  );
};

export const calculateYourResponsibility = (
  healthDeductible: number | string,
  msaYearlyDeposit: number
): number => {
  let deductible = healthDeductible;

  // The annual_deductible property is a string that has text (i.e. "$9,400 annual deductible")
  if (typeof deductible === "string") {
    deductible = deductible.replace(/\D/g, "");
  }

  const result = Number(deductible) - msaYearlyDeposit;

  return result < 0 ? 0 : result;
};

export const shouldSuppressDrugPricing = (
  plan: Plan | SearchResultPlan
): boolean => {
  const { redactions } = plan;

  return redactions.some(
    redaction =>
      redaction.type === "suppression" &&
      ["formulary", "partd", "pharmacy"].includes(redaction.issue)
  );
};

export const getNewLISCodeFromUserOptions = (
  helpType: LowIncomeSubsidyOption | undefined,
  amountOfSSHelp: LowIncomeSubsidyAmount | null
): LowIncomeSubsidyStatus | null => {
  if (
    helpType === LowIncomeSubsidyOption.Medicaid ||
    helpType === LowIncomeSubsidyOption.SSI
  ) {
    return LowIncomeSubsidyStatus.LIS_LEVEL_1A;
  }
  if (helpType === LowIncomeSubsidyOption.MSP) {
    return LowIncomeSubsidyStatus.LIS_LEVEL_1B;
  }
  if (helpType === LowIncomeSubsidyOption.Unsure) {
    return LowIncomeSubsidyStatus.LIS_NO_HELP;
  }
  if (
    helpType === LowIncomeSubsidyOption.SS &&
    amountOfSSHelp === LowIncomeSubsidyAmount.HelpNA
  ) {
    return LowIncomeSubsidyStatus.LIS_LEVEL_1C;
  }
  if (
    helpType === LowIncomeSubsidyOption.SS &&
    amountOfSSHelp === LowIncomeSubsidyAmount.Help0
  ) {
    return LowIncomeSubsidyStatus.LIS_LEVEL_4_100;
  }
  if (
    helpType === LowIncomeSubsidyOption.SS &&
    amountOfSSHelp === LowIncomeSubsidyAmount.Help25
  ) {
    return LowIncomeSubsidyStatus.LIS_LEVEL_4_75;
  }
  if (
    helpType === LowIncomeSubsidyOption.SS &&
    amountOfSSHelp === LowIncomeSubsidyAmount.Help50
  ) {
    return LowIncomeSubsidyStatus.LIS_LEVEL_4_50;
  }
  if (
    helpType === LowIncomeSubsidyOption.SS &&
    amountOfSSHelp === LowIncomeSubsidyAmount.Help75
  ) {
    return LowIncomeSubsidyStatus.LIS_LEVEL_4_25;
  }
  if (
    helpType === LowIncomeSubsidyOption.SS &&
    amountOfSSHelp === LowIncomeSubsidyAmount.HelpUnsure
  ) {
    return LowIncomeSubsidyStatus.LIS_LEVEL_4_100;
  }
  return LowIncomeSubsidyStatus.LIS_NO_HELP;
};

export const fetchPlanIdForNextYear = async (
  coverage: CoverageInfo,
  origin: string
): Promise<FullPlanId | undefined> => {
  const parsedCoverage = {
    ...coverage,
    contract_year: coverage.coverage_year,
  };

  if (
    !parsedCoverage.contract_id ||
    !parsedCoverage.contract_year ||
    !parsedCoverage.plan_id ||
    !parsedCoverage.segment_id
  ) {
    return undefined;
  }

  try {
    const nextYearPlanId = await getPlanInfoForNextYear(
      parsePlanLongIdIntoUrlPartForApi(makePlanLongId(parsedCoverage))
    );
    return nextYearPlanId;
  } catch (e) {
    logError(`Could not get plan info for next year (${origin})`, e);
    return undefined;
  }
};

export const getAdditionalBenefit = (
  plan: Plan,
  type: string,
  category: string,
  benefit: string
): OtherBenefit | null => {
  const cat = `SB_CAT_${category.toUpperCase()}`;
  const bene = `SB_${benefit.toUpperCase()}`;

  if (plan.additional_supplemental_benefits) {
    const otherCategory = plan.additional_supplemental_benefits[type].find(
      (benefit: OtherBenefitCategory) => benefit.category === cat
    );

    if (otherCategory) {
      const otherBenefit = otherCategory.benefits.find(
        (benefit: OtherBenefit) => benefit.benefit === bene
      );

      if (otherBenefit) {
        return otherBenefit;
      }
    }
  }

  return null;
};
